import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { PlaceholderBanner } from "@/components/common/PlaceholderBanner";
import { Plug, Circle } from "lucide-react";

const CATEGORY_LABEL = {
  crm: "CRM", core_banking: "Core Banking", insurance: "Insurance", learning: "Learning",
  support: "Customer Support", complaints: "Complaints", identity: "Identity",
  document: "Document Management", notification: "Notification",
};

export default function IntegrationsPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/integrations").then(r => setRows(r.data)); }, []);

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach(r => { (g[r.category] = g[r.category] || []).push(r); });
    return g;
  }, [rows]);

  return (
    <div data-testid={TID.integrationsPage} className="space-y-6 animate-fade-in-up">
      <header className="flex items-end justify-between border-b border-border pb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Integration framework</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-altara-ink mt-1">Connectors to existing enterprise systems</h1>
          <p className="text-sm text-muted-foreground mt-1">The platform never replaces enterprise systems. It consumes events and enriches intelligence.</p>
        </div>
        <div className="font-mono text-[10px] text-muted-foreground">{rows.length} connectors</div>
      </header>

      <PlaceholderBanner
        title="All connectors are currently MOCKED"
        message="Real credentials are not wired. Some connectors are shown in MOCKED mode (emitting synthetic events during seed) so you can visualise volume; the rest are NOT_CONNECTED placeholders. Wire real credentials via the Integration Framework when your tenant is ready."
      />

      <div className="space-y-6">
        {Object.entries(grouped).map(([cat, list]) => (
          <div key={cat}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-2">{CATEGORY_LABEL[cat] || cat}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {list.map(c => <ConnectorCard key={c.connector_id} c={c} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConnectorCard({ c }) {
  const isMocked = c.status === "mocked";
  return (
    <div className={`border rounded-sm bg-white p-4 ${isMocked ? "border-altara-teal/50" : "border-slate-200"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 flex items-center justify-center rounded-sm ${isMocked ? "bg-teal-50 text-altara-teal" : "bg-slate-50 text-slate-500"}`}>
            <Plug className="w-4 h-4" />
          </div>
          <div>
            <div className="font-medium text-sm text-slate-800 leading-tight">{c.display_name}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{c.vendor}</div>
          </div>
        </div>
        <StatusPill status={c.status} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] font-mono text-slate-500">
        <div>
          <div className="uppercase tracking-widest text-slate-400">Events / 24h</div>
          <div className="text-slate-800 text-sm tabular-nums">{c.events_last_24h}</div>
        </div>
        <div>
          <div className="uppercase tracking-widest text-slate-400">Last event</div>
          <div className="text-slate-800">{c.last_event_at ? new Date(c.last_event_at).toLocaleString() : "—"}</div>
        </div>
      </div>
      <div className="mt-3 text-[10px] text-slate-500">{c.notes}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    mocked: { bg: "bg-teal-50", text: "text-teal-800", border: "border-teal-200", label: "Mocked" },
    connected: { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", label: "Connected" },
    not_connected: { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200", label: "Not connected" },
    error: { bg: "bg-rose-50", text: "text-rose-800", border: "border-rose-200", label: "Error" },
  };
  const s = map[status] || map.not_connected;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] font-medium border rounded-sm ${s.bg} ${s.text} ${s.border}`}>
      <Circle className="w-2 h-2 fill-current" />
      {s.label}
    </span>
  );
}
